import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Trash2, Plus, GripVertical, Pencil, Check } from 'lucide-react';

function ChecklistItemRow({ item, canEdit, onToggle, onRemove, onEdit, onDragStart, onDragOver, onDrop, isDragging }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);

  const commit = () => {
    const trimmed = text.trim();
    if (trimmed && trimmed !== item.text) onEdit(item.id, trimmed);
    else setText(item.text);
    setEditing(false);
  };

  return (
    <div
      className={`flex items-center gap-2 bg-[#F5F5F5] rounded px-2 py-1.5 ${isDragging ? 'opacity-40' : ''}`}
      draggable={canEdit && !editing}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`checklist-item-${item.id}`}
    >
      {canEdit && (
        <GripVertical size={14} className="text-[#C0C0C0] cursor-grab active:cursor-grabbing flex-shrink-0" data-testid={`checklist-drag-${item.id}`} />
      )}
      <input type="checkbox" checked={!!item.done} onChange={() => onToggle(item.id)} className="rounded cursor-pointer flex-shrink-0" data-testid={`checklist-toggle-${item.id}`} />
      {editing ? (
        <>
          <Input
            value={text}
            autoFocus
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { setText(item.text); setEditing(false); }
            }}
            className="h-7 text-sm flex-1"
            data-testid={`checklist-edit-input-${item.id}`}
          />
          <button onClick={commit} className="text-[#059669] hover:text-[#047857] flex-shrink-0" data-testid={`checklist-edit-save-${item.id}`}>
            <Check size={14} />
          </button>
        </>
      ) : (
        <>
          <span className={`text-sm flex-1 ${item.done ? 'line-through text-[#999999]' : 'text-[#111111]'}`}>{item.text}</span>
          {canEdit && (
            <button onClick={() => { setText(item.text); setEditing(true); }} className="text-[#999999] hover:text-[#FF6B35] flex-shrink-0" data-testid={`checklist-edit-${item.id}`}>
              <Pencil size={13} />
            </button>
          )}
        </>
      )}
      {canEdit && !editing && (
        <button onClick={() => onRemove(item.id)} className="text-[#999999] hover:text-[#DC2626] flex-shrink-0" data-testid={`checklist-remove-${item.id}`}>
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

export function ChecklistEditor({ checklist = [], onToggle, onAdd, onRemove, onEdit, onReorder, canEdit }) {
  const [newItem, setNewItem] = useState('');
  const [dragId, setDragId] = useState(null);
  const doneCount = checklist.filter(c => c.done).length;

  const submitNewItem = () => {
    if (newItem.trim()) {
      onAdd(newItem.trim());
      setNewItem('');
    }
  };

  return (
    <div data-testid="checklist-editor">
      <Label>
        Checklist{checklist.length > 0 && <span className="text-[#999999] font-normal"> ({doneCount}/{checklist.length})</span>}
      </Label>
      {checklist.length > 0 && (
        <div className="space-y-1 mt-1">
          {checklist.map(item => (
            <ChecklistItemRow
              key={item.id}
              item={item}
              canEdit={canEdit}
              isDragging={dragId === item.id}
              onToggle={onToggle}
              onRemove={onRemove}
              onEdit={onEdit}
              onDragStart={(e) => { setDragId(item.id); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={(e) => { if (dragId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (dragId && dragId !== item.id) onReorder(dragId, item.id);
                setDragId(null);
              }}
            />
          ))}
        </div>
      )}
      {canEdit && (
        <div className="flex gap-2 mt-2">
          <Input
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNewItem(); } }}
            placeholder="Add checklist item"
            data-testid="checklist-new-input"
          />
          <Button variant="outline" onClick={submitNewItem} data-testid="checklist-add-btn">
            <Plus size={16} />
          </Button>
        </div>
      )}
    </div>
  );
}

// Helpers that operate on a checklist array — shared by all modals.
export const checklistOps = {
  toggle: (list, id) => list.map(c => (c.id === id ? { ...c, done: !c.done } : c)),
  add: (list, text) => [...list, { id: crypto.randomUUID(), text, done: false }],
  remove: (list, id) => list.filter(c => c.id !== id),
  edit: (list, id, text) => list.map(c => (c.id === id ? { ...c, text } : c)),
  reorder: (list, fromId, toId) => {
    const items = [...list];
    const from = items.findIndex(c => c.id === fromId);
    const to = items.findIndex(c => c.id === toId);
    if (from < 0 || to < 0) return list;
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    return items;
  },
};
